/**
 * Inline rename of a Draft concept group's title (OR-6657). A published group has no
 * rename affordance, so this only ever backs the Draft surfaces.
 *
 * Returns message ids rather than formatted strings so the hook stays intl-free; the
 * header formats them. Empty and over-length are answered locally; uniqueness only on
 * commit, since the server is the only judge of it.
 *
 * TODO: cleanup with concept_group_rename ff
 */

import { useCallback, useState } from 'react';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

import { CONCEPT_GROUP_NAME_MAX_LENGTH } from '../components/concept-group/hooks/useCreateConceptGroupModal';
import {
  classifyRenameFailure,
  useRenameConceptGroupMutation,
} from '../lib/api/useRenameConceptGroupMutation';

const ERROR_MESSAGE_IDS = {
  required: 'epiChat.conceptGroupName.required',
  tooLong: 'epiChat.conceptGroupName.tooLong',
  duplicate: 'epiChat.conceptGroupName.duplicate',
  conflict: 'epiChat.conceptGroupName.conflict',
  error: 'epiChat.conceptGroupName.renameError',
} as const;

export interface ConceptGroupRenameModel {
  /** Flag + permission gate — when false the title renders read-only. */
  canRename: boolean;
  isEditing: boolean;
  editedName: string;
  /** Message id for the inline name error, or `null`. */
  errorId: string | null;
  isSaving: boolean;
  maxLength: number;
  /** False while the typed name still matches the committed one — nothing to save. */
  canCommit: boolean;
  startEdit: () => void;
  changeName: (value: string) => void;
  cancelEdit: () => void;
  commit: () => Promise<void>;
}

export const useConceptGroupRename = ({
  projectId,
  conceptGroupId,
  currentName,
  canEdit,
  previewId,
}: {
  projectId: string;
  /** The concept group's stable id — the rename endpoint's address. */
  conceptGroupId: string | null;
  currentName: string;
  canEdit: boolean;
  /** Draft only — its preview id, so the Draft review surface picks up the new name. */
  previewId?: string | null;
}): ConceptGroupRenameModel => {
  const renameEnabled = useFeatureFlagSplit('concept_group_rename', false) as boolean;
  const rename = useRenameConceptGroupMutation(projectId);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [errorId, setErrorId] = useState<string | null>(null);

  const startEdit = useCallback(() => {
    setEditedName(currentName);
    setErrorId(null);
    setIsEditing(true);
  }, [currentName]);

  const changeName = useCallback((value: string) => {
    setEditedName(value);
    setErrorId(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setErrorId(null);
  }, []);

  const commit = useCallback(async () => {
    const trimmed = editedName.trim();
    if (!trimmed) {
      setErrorId(ERROR_MESSAGE_IDS.required);
      return;
    }
    if (trimmed.length > CONCEPT_GROUP_NAME_MAX_LENGTH) {
      setErrorId(ERROR_MESSAGE_IDS.tooLong);
      return;
    }
    if (trimmed === currentName || !conceptGroupId) {
      setIsEditing(false);
      return;
    }
    try {
      await rename.mutateAsync({ conceptGroupId, conceptGroupName: trimmed, previewId });
      setIsEditing(false);
      setErrorId(null);
    } catch (error) {
      // The field stays open with the typed text so it can be corrected in place.
      setErrorId(ERROR_MESSAGE_IDS[classifyRenameFailure(error)]);
    }
  }, [editedName, currentName, conceptGroupId, previewId, rename]);

  return {
    canRename: renameEnabled && canEdit,
    isEditing,
    editedName,
    errorId,
    isSaving: rename.isPending,
    maxLength: CONCEPT_GROUP_NAME_MAX_LENGTH,
    canCommit: editedName.trim() !== currentName.trim(),
    startEdit,
    changeName,
    cancelEdit,
    commit,
  };
};
